import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import omit from 'lodash/omit';
import cn from 'classnames';
import { LoadingSpinner } from 'legacy-packages/ft-cb';
import Icon from 'chameleon/ui-stack/ui/base/icon';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import { toast } from 'chameleon/ui-stack/ui/toast';
import ShareLinkDisabledModal from 'chameleon/src/components/SharedAssessmentPage/ShareLinkDisabledModal';
import LeftModalContent from '../LeftModalContent';
import RightModalContent from '../RightModalContent';
import {
  PREVIEW_NOW,
  PREVIEW_LATER,
  MAKE_COPY,
  DECLINE,
  UNAUTHORIZED_ACCESS_SHARED_ASSESSMENT,
  UNAUTHORIZED_ADMIN_ACCESS_SHARED_ASSESSMENT,
} from '../constants';
import { sharedQuizActionEvent } from '../telemetry';
import { hasSubjectAccess } from '../utils';
import { HAS_COPY_INFO_TEXT, HAS_PENDING_COPY_INFO_TEXT } from './constants';

import './style.scss';

/** This component is being used to render both the landing modal seen by the receiver of a shared quiz and the
 * quiz summary. For the quiz summary only the left side of the modal is rendered.
 */
const SharedAssessmentModal = (props) => {
  const {
    itemData,
    getItemData,
    itemFetchId,
    router,
    subjectInfo: { masterSubjectId, id: defaultSubjectId },
    adminSubjects,
    teacherSubjects,
    isTeacher,
    isAdmin,
    isCoordinator,
    client,
    copyItemAndCreateItemShare,
    deleteItemShare,
    schoolId,
    userTimezone,
    updateQuizDescription,
    isLoadingItemShares,
  } = props;

  const {
    itemSharedDate,
    createdBy,
    isFetching,
    isFetched,
    itemInfo,
    itemMetadata,
    tags,
    itemId,
    itemShareId,
    hasCopy,
    hasPendingCopy,
    isTeacherCreated,
    isDisabled,
  } = itemData;

  const { title, description, lastModifiedDate, isOwner, createdDate } =
    itemInfo || {};
  const { location, params } = router;
  const { query, pathname, state: routerState } = location;
  const { subject: subjectId } = params;
  let hasCopyInfoText = hasPendingCopy ? HAS_PENDING_COPY_INFO_TEXT : '';
  hasCopyInfoText =
    hasCopy && !hasPendingCopy ? HAS_COPY_INFO_TEXT : hasCopyInfoText;
  const { isQuizSummary } = routerState || {};

  const [currentAction, setCurrentAction] = useState(PREVIEW_NOW);
  const [assessmentCopyName, setAssessmentCopyName] = useState(title);
  const [isShareLinkDisabled, setIsShareLinkDisabled] = useState(false);

  const shouldShowRightContent = isQuizSummary ? isTeacherCreated : true;

  useEffect(() => {
    setAssessmentCopyName(title);
  }, [itemInfo]);

  useEffect(() => {
    const userHasSubjectAccess = hasSubjectAccess({
      subjectId,
      isTeacher,
      isAdmin,
      isCoordinator,
      teacherSubjects,
      adminSubjects,
    });
    if (itemFetchId && userHasSubjectAccess) {
      getItemData(client, itemFetchId, schoolId, subjectId);
    } else {
      const redirectRoute = isAdmin
        ? '/admin'
        : isCoordinator
          ? '/coordinator'
          : '/subjects/preview';
      const message =
        isAdmin || isCoordinator
          ? UNAUTHORIZED_ADMIN_ACCESS_SHARED_ASSESSMENT
          : UNAUTHORIZED_ACCESS_SHARED_ASSESSMENT;
      router.push(redirectRoute);
      toast(message, { timeout: 5 });
    }
  }, [itemFetchId]);

  useEffect(() => {
    if (isDisabled || (isFetched && !itemData.itemId)) {
      setIsShareLinkDisabled(true);
    }
  }, [isDisabled, itemData]);

  const handleCloseModal = () => {
    router.push({
      pathname,
      query: omit(query, ['shareKey', 'quizSummaryId']),
    });
  };

  const handleDeclineShare = () => {
    if (itemShareId) {
      return deleteItemShare(client, itemShareId, handleCloseModal);
    }
    return handleCloseModal();
  };

  const handlePreviewLater = () => {
    const shouldCreateItemShareOnly = true;
    copyItemAndCreateItemShare(
      client,
      itemId,
      subjectId,
      itemFetchId,
      handleCloseModal,
      shouldCreateItemShareOnly,
      itemShareId,
    );
  };

  const handleMakeCopy = () => {
    const shouldCreateItemShareOnly = false;
    copyItemAndCreateItemShare(
      client,
      itemId,
      subjectId,
      itemFetchId,
      handleCloseModal,
      shouldCreateItemShareOnly,
      itemShareId,
      assessmentCopyName,
    );
  };

  const handlePreviewNow = () => {
    router.push({
      pathname,
      query: {
        ...omit(query, ['shareKey', 'quizSummaryId']),
        isPreview: true,
        itemId,
        itemShareId,
        itemTitle: assessmentCopyName,
        itemShareKey: itemFetchId,
      },
    });
  };

  const handleUpdateDescription = (newDescription, closeEdit) => {
    const variables = {
      item_id: itemId,
      description: newDescription,
    };
    updateQuizDescription(client, variables, closeEdit);
  };

  const actionHandlers = {
    [PREVIEW_NOW]: handlePreviewNow,
    [PREVIEW_LATER]: handlePreviewLater,
    [MAKE_COPY]: handleMakeCopy,
    [DECLINE]: handleDeclineShare,
  };

  const handleSubmit = () => {
    const currentActionHandler = actionHandlers[currentAction];
    currentActionHandler();
    sharedQuizActionEvent({
      subjectId,
      quizId: itemId,
      action: currentAction,
    });
  };

  const renderModalContent = () => (
    <Fragment>
      {hasCopyInfoText && (
        <div className="grid grid-flow-col justify-start gap-x-2 text-14 bg-blue-100 text-gray-700 py-6 px-4">
          <Icon
            width={18}
            height={18}
            id="info_outline_filled"
            className="text-blue-700"
          />
          {hasCopyInfoText}
        </div>
      )}
      <div
        className={cn('modal-content-wrapper', {
          'modal-content-wrapper--plain': !shouldShowRightContent,
        })}
      >
        <LeftModalContent
          createdBy={createdBy}
          lastModifiedDate={lastModifiedDate}
          itemSharedDate={itemSharedDate}
          itemMetadata={itemMetadata}
          tags={tags}
          userTimezone={userTimezone}
          createdDate={createdDate}
          masterSubjectId={masterSubjectId || defaultSubjectId}
        />
        {shouldShowRightContent && (
          <RightModalContent
            description={description}
            currentAction={currentAction}
            setCurrentAction={setCurrentAction}
            assessmentCopyName={assessmentCopyName}
            setAssessmentCopyName={setAssessmentCopyName}
            isQuizSummary={isQuizSummary}
            isQuizOwner={isOwner}
            handleUpdateDescription={handleUpdateDescription}
          />
        )}
      </div>
    </Fragment>
  );

  if (isShareLinkDisabled) {
    return <ShareLinkDisabledModal closeModal={handleCloseModal} />;
  }

  const hideButtons = isFetching || isQuizSummary;
  if ((!isFetched && !isFetching) || (isFetched && !itemData.itemId)) {
    return null;
  }

  let modalContent;
  if (!hideButtons) {
    modalContent = {
      btnPrimary: isLoadingItemShares ? (
        <LoadingSpinner containerClassName="flex" height="20px" />
      ) : (
        'OK'
      ),
    };
    if (!isFetching) {
      modalContent.btnSecondary = 'Cancel';
    }
  }

  return (
    <ConfirmDialog
      size="big"
      title={
        title
          ? `${title} ${isQuizSummary ? 'Summary' : 'has been shared with you'}`
          : ''
      }
      // disable save button if assessmentCopyName is not provided}
      isPrimaryBtnDisabled={
        isLoadingItemShares ||
        (currentAction === MAKE_COPY && !assessmentCopyName)
      }
      onSuccess={handleSubmit}
      onClose={handleCloseModal}
      content={modalContent}
      wrapperClassName={cn({
        'SharedModal--dialog--is-loading': isFetching,
        'SharedModal--dialog': !isFetching,
        'SharedModal--dialog--small': !isFetching && !shouldShowRightContent,
        'z-100': isQuizSummary,
      })}
      dialogClassName="shared-assessment-modal-wrapper"
    >
      {isFetching ? <LoadingSpinner /> : renderModalContent()}
    </ConfirmDialog>
  );
};

SharedAssessmentModal.propTypes = {
  itemData: PropTypes.objectOf(PropTypes.any).isRequired,
  subjectInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  getItemData: PropTypes.func.isRequired,
  itemFetchId: PropTypes.string.isRequired,
  router: PropTypes.objectOf(PropTypes.any).isRequired,
  teacherSubjects: PropTypes.arrayOf(PropTypes.any).isRequired,
  copyItemAndCreateItemShare: PropTypes.func.isRequired,
  deleteItemShare: PropTypes.func,
  schoolId: PropTypes.string.isRequired,
  updateQuizDescription: PropTypes.func,
};

SharedAssessmentModal.defaultProps = {
  copyItemAndCreateItemShare: () => {},
  deleteItemShare: () => {},
  updateQuizDescription: () => {},
};

export default SharedAssessmentModal;
